export type VpnProfile = {
  id: string
  label: string
  enabled: boolean
  revision: number
  status: string
  lastError?: string | null
  desiredState?: "connected" | "disconnected"
  generation?: string | number
  peerAddress?: string
  peerId?: string
  localId?: string
  sourceAddress?: string
  gatewayAddress?: string | null
  remoteCidrs?: string[]
  allowedTargets?: { host: string; ports: number[] }[]
  dnsServers?: string[]
  hasSecret?: boolean
}

export type VpnDependency = { id: string; label: string; connector?: string; platform?: string }

const splitLines = (value: string) => value.split(/[,\n]/).map((part) => part.trim()).filter(Boolean)
const isIpv4 = (value: string) => /^(0|[1-9]\d{0,2})(\.(0|[1-9]\d{0,2})){3}$/.test(value) && value.split(".").every((part) => Number(part) <= 255)

/** A simple host:port list keeps the form readable without accepting commands
 * or executable VPN configuration. The API applies the authoritative policy. */
export function parseVpnTargets(value: string): NonNullable<VpnProfile["allowedTargets"]> {
  return value.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const match = /^([a-zA-Z0-9][a-zA-Z0-9.-]*):([\d,\s]+)$/.exec(line)
    if (!match) throw new Error("Enter each allowed destination as hostname:port,port, for example sap.internal:3301,44300.")
    const ports = match[2]!.split(",").map((port) => Number(port.trim()))
    if (ports.some((port) => !Number.isInteger(port) || port < 1 || port > 65535)) throw new Error("Destination ports must be between 1 and 65535.")
    return { host: match[1]!, ports: [...new Set(ports)] }
  })
}

export function vpnProfileBody(form: FormData) {
  const text = (key: string) => String(form.get(key) ?? "").trim()
  const label = text("label")
  const peerAddress = text("peerAddress")
  const peerId = text("peerId")
  if (!label || !peerId) throw new Error("Enter a VPN name and customer gateway identity.")
  if (!isIpv4(peerAddress)) throw new Error("Enter the customer gateway's public IPv4 address.")
  const remoteCidrs = splitLines(text("remoteCidrs"))
  if (!remoteCidrs.length || remoteCidrs.some((cidr) => {
    const [address, prefix, extra] = cidr.split("/")
    return !address || !isIpv4(address) || prefix === undefined || !/^\d{1,2}$/.test(prefix) || Number(prefix) < 1 || Number(prefix) > 32 || extra !== undefined
  })) throw new Error("Enter customer networks in IPv4 CIDR format, for example 192.168.150.0/24.")
  const dnsServers = splitLines(text("dnsServers"))
  if (dnsServers.some((address) => !isIpv4(address))) throw new Error("DNS servers must be IPv4 addresses.")
  const sourceAddress = text("sourceAddress")
  if (sourceAddress && !isIpv4(sourceAddress)) throw new Error("The tunnel source must be an IPv4 address.")
  const allowedTargets = parseVpnTargets(text("allowedTargets"))
  if (!allowedTargets.length) throw new Error("Add at least one permitted destination and port.")
  const secret = String(form.get("secret") ?? "")
  return {
    label, peerAddress, peerId, remoteCidrs, allowedTargets, dnsServers,
    enabled: form.get("enabled") === "on",
    ...(sourceAddress ? { sourceAddress } : {}),
    ...(secret ? { secret } : {}),
  }
}

export function vpnChangeMessage(action: string, dependencies: VpnDependency[]) {
  const affected = dependencies.length
    ? dependencies.map((connection) => `• ${connection.label} (${connection.connector ?? connection.platform ?? "connection"})`).join("\n")
    : "No saved product connections use this VPN."
  return `${action}\n\nAffected connections:\n${affected}\n\nActive requests through this VPN may stop. Requests will not switch to Direct automatically.`
}

export function vpnStatusLabel(profile: Pick<VpnProfile, "enabled" | "status">) {
  if (!profile.enabled) return "Disabled"
  return profile.status ? profile.status.replaceAll("_", " ") : "Not checked"
}

export function connectionFormBody(form: FormData, platform: string) {
  const body: Record<string, unknown> = {}
  form.forEach((value, key) => {
    if (["password", "clientSecret", "apiToken"].includes(key) ? String(value).length > 0 : String(value).trim()) body[key] = value
  })
  body.active = form.get("active") === "on"
  body.isDefault = form.get("isDefault") === "on"
  if (platform === "sap-commerce") {
    body.vpnProfileId = String(form.get("vpnProfileId") ?? "").trim() || null
    if (form.has("portalConnectionId")) body.portalConnectionId = String(form.get("portalConnectionId") ?? "").trim() || null
    if (form.has("baseUrl")) body.baseUrl = String(form.get("baseUrl") ?? "").trim()
  } else {
    delete body.vpnProfileId
    delete body.portalConnectionId
  }
  return body
}
